import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { alpha, useTheme } from "@mui/material/styles";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Stack,
  Button,
  TextField,
  MenuItem,
  Typography,
  Alert,
  IconButton,
} from "@mui/material";
import {
  Close,
  ReceiptLongOutlined,
  PhotoCameraOutlined,
  CheckRounded,
} from "@mui/icons-material";
import { branchCashExpense } from "api/branchCash";

const CURRENCIES = ["THB", "LAK", "USD"];
// Independent flat per-currency ceilings — NOT exchange-rate equivalents of each
// other — must match backend EXPENSE_THRESHOLDS (controllers/financial.js).
const EXPENSE_THRESHOLDS = { THB: 300, LAK: 200000, USD: 10 };

// Branch staff record a cash expense (e.g. buying drinking water) at their own
// station: currency + amount + reason + a receipt photo, all required. At/below
// the per-currency threshold it settles immediately; above it, files a
// pending_approval request a manager must clear (see approvals.jsx / mobile).
export default function BranchExpenseDialog({ open, onClose, station, onSuccess }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const accent = theme.palette.secondary.main;

  const [currency, setCurrency] = useState("THB");
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setCurrency("THB");
      setAmount("");
      setReason("");
      setFile(null);
      setError("");
      setSubmitting(false);
    }
  }, [open]);

  const amt = Number(amount) || 0;
  const requiresApproval = amt > (EXPENSE_THRESHOLDS[currency] || 0);

  const handleSubmit = async () => {
    if (!station?.id || !amt || amt <= 0) return setError(t("branchCash.invalidAmount"));
    if (!reason.trim()) return setError(t("branchCash.expenseReasonRequired"));
    if (!file) return setError(t("branchCash.expenseReceiptRequired"));
    setSubmitting(true);
    setError("");
    const fd = new FormData();
    fd.append("stationId", station.id);
    fd.append("currency", currency);
    fd.append("amount", String(amt));
    fd.append("reason", reason.trim());
    fd.append("receiptPhoto", file);
    const res = await branchCashExpense(fd);
    setSubmitting(false);
    if (res && res.success) {
      onSuccess && onSuccess(res);
    } else {
      setError((res && res.message) || t("common.error"));
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "20px" } }}>
      <DialogContent sx={{ p: { xs: 2.25, sm: 3 } }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 0.5 }}>
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Box sx={{ width: 40, height: 40, borderRadius: "12px", display: "flex", alignItems: "center", justifyContent: "center", background: alpha(accent, 0.1), color: accent, flexShrink: 0 }}>
              <ReceiptLongOutlined />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 800, fontSize: 18, lineHeight: 1.15 }}>{t("branchCash.expenseTitle")}</Typography>
              <Typography sx={{ fontSize: 12.5 }} color="text.secondary">{station?.name || station?.id}</Typography>
            </Box>
          </Stack>
          <IconButton size="small" onClick={onClose} disabled={submitting}><Close fontSize="small" /></IconButton>
        </Box>

        <Stack spacing={1.75} sx={{ mt: 2.5 }}>
          <Stack direction="row" spacing={1.5}>
            <TextField select size="small" color="secondary" label={t("branchCash.currency")} value={currency} onChange={(e) => setCurrency(e.target.value)} sx={{ width: 120 }}>
              {CURRENCIES.map((c) => (
                <MenuItem key={c} value={c}>{c}</MenuItem>
              ))}
            </TextField>
            <TextField fullWidth size="small" type="number" color="secondary" label={t("branchCash.amount")} value={amount} onChange={(e) => setAmount(e.target.value)} />
          </Stack>

          <TextField
            fullWidth multiline minRows={2} size="small" color="secondary"
            label={t("branchCash.expenseReason")}
            placeholder={t("branchCash.expenseReasonPlaceholder")}
            value={reason} onChange={(e) => setReason(e.target.value)}
          />

          {requiresApproval && (
            <Alert severity="info" sx={{ borderRadius: "12px" }}>
              {t("branchCash.expenseNeedsApproval", { threshold: `${EXPENSE_THRESHOLDS[currency]} ${currency}` })}
            </Alert>
          )}

          {/* receipt upload */}
          <Button variant={file ? "contained" : "outlined"} color="secondary" component="label" startIcon={file ? <CheckRounded /> : <PhotoCameraOutlined />} sx={{ borderRadius: "12px", fontWeight: 700, textTransform: "none", justifyContent: "flex-start", py: 1.25 }}>
            {file ? t("branchCash.receiptAttached") : t("branchCash.attachReceipt")}
            <input hidden accept="image/*" type="file" onChange={(e) => setFile(e.target.files[0] || null)} />
          </Button>

          {error && <Alert severity="error" sx={{ borderRadius: "12px" }}>{error}</Alert>}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: { xs: 2.25, sm: 3 }, pb: 2.5, pt: 0 }}>
        <Button variant="outlined" color="secondary" onClick={onClose} disabled={submitting} sx={{ borderRadius: "12px", fontWeight: 700, px: 2.5 }}>
          {t("common.cancel")}
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button variant="contained" color="secondary" onClick={handleSubmit} disabled={submitting || !amt || !reason.trim() || !file} sx={{ borderRadius: "12px", fontWeight: 700, px: 3 }}>
          {requiresApproval ? t("branchCash.submitForApproval") : t("branchCash.confirmExpense")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
